/**
 * Общая форма создания и редактирования фотографии.
 *
 * Создание и правка отличаются только заголовком и тем, что при создании файл
 * обязателен, поэтому поля описаны один раз.
 */
import { Create, Edit, useForm } from "@refinedev/antd";
import { Col, Form, Input, InputNumber, Row, Select } from "antd";

import { ImageUpload } from "../../components/ImageUpload";
import { LocationPicker } from "../../components/LocationPicker";
import { CONTENT_STATUSES } from "../../lib/constants";

interface PhotoForm {
  objectKey?: string;
  imageUrl?: string;
  location?: { latitude: number; longitude: number };
  area?: { latitude: number; longitude: number }[];
  year?: number;
  yearTo?: number;
  description?: string;
  sourceUrl?: string;
  status?: string;
  moderationComment?: string;
}

const CURRENT_YEAR = new Date().getFullYear();
// Нижняя граница шкалы: игра берёт не только фотографию, но и живопись,
// поэтому отсчёт идёт от начала нашей эры. Значение должно совпадать с
// MIN_YEAR на сервере — иначе панель предложит год, который тот отвергнет.
const MIN_YEAR = 0;

/**
 * Карта, пишущая в форму и точку, и область.
 *
 * Отдельным компонентом, потому что Form.Item с одним name не умеет держать
 * два значения, а Form.useFormInstance доступен только внутри формы.
 */
function PlacePicker() {
  const form = Form.useFormInstance<PhotoForm>();
  const location = Form.useWatch("location", form);
  const area = Form.useWatch("area", form) ?? [];

  return (
    <LocationPicker
      value={location ?? null}
      onChange={(next) => form.setFieldValue("location", next)}
      area={Array.isArray(area) ? area : []}
      onAreaChange={(next) => form.setFieldValue("area", next)}
    />
  );
}

function Fields({ previewUrl, requireImage }: { previewUrl?: string; requireImage: boolean }) {
  return (
    <Row gutter={24}>
      <Col xs={24} lg={11}>
        <Form.Item
          label="Изображение"
          name="objectKey"
          rules={requireImage ? [{ required: true, message: "Загрузите изображение" }] : []}
        >
          <ImageUpload previewUrl={previewUrl} />
        </Form.Item>

        <Form.Item label="Описание" name="description">
          <Input.TextArea rows={3} placeholder="Что изображено на снимке" />
        </Form.Item>

        <Form.Item
          label="Источник"
          name="sourceUrl"
          // Раньше требовался URL, но источник далеко не всегда ссылка: у
          // архивного снимка это книга со страницей, у картины — музей с
          // инвентарным номером. Требование URL заставляло либо выдумывать
          // ссылку, либо оставлять поле пустым — и атрибуция терялась.
          tooltip="Ссылка, книга со страницей, музей, условия лицензии — что угодно, по чему можно найти оригинал. Игрок видит это только после ответа"
          rules={[{ max: 500, message: "Не длиннее 500 символов" }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="Библиотека Конгресса США, коллекция Прокудина-Горского, 1911"
          />
        </Form.Item>
      </Col>

      <Col xs={24} lg={13}>
        {/* Точка и область правятся одной картой: это ответ на один и тот же
            вопрос «где», и разносить их по двум полям значило бы предлагать
            модератору выбрать дважды. Область хранится отдельным полем формы,
            поэтому карта здесь без name — она пишет оба значения сама. */}
        <Form.Item
          label="Место съёмки"
          shouldUpdate={(prev, next) => prev.location !== next.location || prev.area !== next.area}
          rules={[
            ({ getFieldValue }) => ({
              validator() {
                const area = getFieldValue("area") ?? [];
                if (area.length >= 3 || getFieldValue("location")) return Promise.resolve();
                return Promise.reject(new Error("Укажите точку или область на карте"));
              },
            }),
          ]}
        >
          {() => <PlacePicker />}
        </Form.Item>

        <Form.Item name="location" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="area" hidden>
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Год съёмки"
              name="year"
              rules={[{ required: true, message: "Укажите год" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={MIN_YEAR}
                max={CURRENT_YEAR}
                placeholder="1935"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Конец периода"
              name="yearTo"
              // У картины или гравюры точной даты не знает и сам архив, зато
              // известно десятилетие. Любой год внутри периода засчитывается
              // игроку как точный ответ, и его об этом предупреждают заранее.
              extra="Пусто или тот же год — точная датировка"
              dependencies={["year"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const from = getFieldValue("year");
                    if (value == null || from == null || value >= from) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Конец периода раньше его начала"));
                  },
                }),
              ]}
            >
              <InputNumber style={{ width: "100%" }} min={MIN_YEAR} max={CURRENT_YEAR} placeholder="1760" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Статус" name="status" initialValue="CONTENT_STATUS_DRAFT">
              <Select
                options={CONTENT_STATUSES.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Комментарий модератора"
          name="moderationComment"
          tooltip="Виден автору снимка — объясните причину отклонения"
        >
          <Input.TextArea rows={2} />
        </Form.Item>
      </Col>
    </Row>
  );
}

export function PhotoCreate() {
  const { formProps, saveButtonProps } = useForm<PhotoForm>({ resource: "photos" });

  return (
    <Create title="Новая фотография" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Fields requireImage />
      </Form>
    </Create>
  );
}

export function PhotoEdit() {
  const { formProps, saveButtonProps, query } = useForm<PhotoForm>({ resource: "photos" });
  const record = query?.data?.data;

  return (
    <Edit title="Редактирование фотографии" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Fields requireImage={false} previewUrl={record?.imageUrl} />
      </Form>
    </Edit>
  );
}
